import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// conversion-actions--bulk-select
//
// Row + section checkboxes drive a bulk action bar over the Manage Conversions list. This controller
// lives on the panel-content div (#ca-management-panel-content-<id>), which the list turbo re-render
// does NOT replace — so the bar survives, while the row checkboxes inside the re-rendered list come
// back unchecked, naturally clearing the selection after a bulk apply.
//
// Selection is always computed from the SEARCH-VISIBLE row checkboxes (list-filter hides non-matching
// rows via inline display:none), so a section select-all and the count never touch filtered-out rows.
export default class extends Controller {
  static targets = ["rowCheckbox", "sectionCheckbox", "bar", "count", "more"]
  static values = { url: String }

  // Re-render of the list swaps every row checkbox → recompute (selection resets to empty).
  rowCheckboxTargetConnected() { this.refresh() }
  rowCheckboxTargetDisconnected() { this.refresh() }

  toggleRow() { this.refresh() }

  // Header select-all: mirror its state onto every visible row checkbox in the same section.
  toggleSection(event) {
    const section = event.target.closest("[data-bulk-section]")
    this.visibleRowCheckboxesIn(section).forEach((cb) => { cb.checked = event.target.checked })
    this.refresh()
  }

  clear() {
    this.rowCheckboxTargets.forEach((cb) => { cb.checked = false })
    this.refresh()
  }

  async apply(event) {
    const ids = this.selectedIds()
    if (ids.length === 0) return
    this.closeMore()

    const label = event.currentTarget.textContent.trim()
    let response
    try {
      response = await patch(this.urlValue, {
        body: JSON.stringify({ operation: event.params.operation, conversion_action_ids: ids }),
        contentType: "application/json",
        responseKind: "turbo-stream",
      })
    } catch {
      // request.js does a bare `await window.fetch`, so a network-level failure THROWS rather than
      // returning a non-ok response. Without this the throw escaped before announce ever ran and the
      // bar kept reading "Bulk updating N items" — the same thing it said before the click, which is
      // the ambiguity the non-2xx branch below exists to remove.
      this.announce(`Couldn't apply "${label}" — nothing changed. Try again.`, true)
      return
    }

    // The response used to be discarded, which made a rejected operation indistinguishable from
    // success — the bar just sat there still holding the selection.
    if (!response.ok) {
      this.announce(`Couldn't apply "${label}" — nothing changed. Try again.`, true)
      return
    }

    // Rows move between sections on re-render, and on a long list that happens off-screen, so say
    // what changed here rather than leaving the reader to hunt for it.
    this.announce(`${label} · ${ids.length} ${ids.length === 1 ? "item" : "items"}`)
    // The list re-render replaces the checkboxes (unchecked) → refresh() empties the selection and
    // hides the bar. Reload any open detail frame so its toggles reflect the bulk change.
    this.reloadDetailFrames()
    // Re-applying the search filter after the re-render is list-filter's own job (it watches its item
    // targets reconnect), so every path that replaces the list is covered, not just this one.
  }

  // Hold a message in the bar for a moment. refresh() runs on every checkbox swap (the re-render
  // fires one per row), so it has to leave the message alone or the confirmation never survives the
  // render that prompted it.
  announce(message, isError = false) {
    if (!this.hasCountTarget) return

    clearTimeout(this.announceTimer)
    this.announcing = true
    this.countTarget.textContent = message
    this.countTarget.classList.toggle("text-sg-error-light", isError)
    if (this.hasBarTarget) this.barTarget.classList.remove("hidden")

    this.announceTimer = setTimeout(() => {
      this.announcing = false
      this.countTarget.classList.remove("text-sg-error-light")
      this.refresh()
    }, 4000)
  }

  refresh() {
    this.syncSectionCheckboxes()
    if (this.announcing) return

    const count = this.selectedIds().length
    if (this.hasCountTarget) {
      this.countTarget.textContent = `Bulk updating ${count} ${count === 1 ? "item" : "items"}`
    }
    if (this.hasBarTarget) this.barTarget.classList.toggle("hidden", count === 0)
    if (count === 0) this.closeMore()
  }

  disconnect() {
    clearTimeout(this.announceTimer)
  }

  syncSectionCheckboxes() {
    this.sectionCheckboxTargets.forEach((box) => {
      const boxes = this.visibleRowCheckboxesIn(box.closest("[data-bulk-section]"))
      const checked = boxes.filter((cb) => cb.checked).length
      box.checked = boxes.length > 0 && checked === boxes.length
      box.indeterminate = checked > 0 && checked < boxes.length
    })
  }

  reloadDetailFrames() {
    this.element.querySelectorAll("turbo-frame[id^='ca-detail-frame-']").forEach((frame) => {
      if (frame.src) frame.reload()
    })
  }

  closeMore() {
    if (this.hasMoreTarget) this.moreTarget.removeAttribute("open")
  }

  // A <details> menu stays open until its own summary is clicked again, which traps the reader.
  // Matches how every other dropdown in the app behaves (see nav_profile_dropdown_controller).
  closeMoreOnOutsideClick(event) {
    if (!this.hasMoreTarget || !this.moreTarget.open) return
    if (this.moreTarget.contains(event.target)) return

    this.closeMore()
  }

  closeMoreOnEscape(event) {
    if (event.key === "Escape") this.closeMore()
  }

  selectedIds() {
    return this.visibleRowCheckboxes().filter((cb) => cb.checked).map((cb) => cb.value)
  }

  visibleRowCheckboxes() {
    return this.rowCheckboxTargets.filter((cb) => this.isSearchVisible(cb))
  }

  visibleRowCheckboxesIn(section) {
    if (!section) return []
    return this.visibleRowCheckboxes().filter((cb) => section.contains(cb))
  }

  isSearchVisible(cb) {
    const item = cb.closest("[data-list-filter-target='item']")
    return !item || item.style.display !== "none"
  }
}
